import React, { useState, useEffect, useRef } from 'react';
import './Landing.css';

// Cross-app links (vers l'application principale)
const AppLink = ({ to, className, children, style }) => (
  <a href={to} className={className} style={style}>{children}</a>
);

const API = process.env.REACT_APP_API_URL || '/api';

const FALLBACK_PLANS = [
  { id: 'f1', name: 'Pay as you go', slug: 'pay_per_user', billing_model: 'per_user', price_monthly: '4.5',  max_sites: '-1', max_users: '-1', max_teams: '-1', trial_days: '14', features: '["Pointage QR Code","Géolocalisation GPS","Supervision temps réel","Gestion des sites et équipes","Historique & export CSV"]' },
  { id: 'f2', name: 'Pro',           slug: 'pro',          billing_model: 'tiered',   price_monthly: '75',   max_sites: '-1', max_users: '-1', max_teams: '-1', included_users: '25', price_per_extra_user: '2.5', trial_days: '14', features: '["Tout inclus","25 utilisateurs inclus","2,50€/utilisateur supplémentaire","Support email prioritaire"]' },
  { id: 'f3', name: 'Entreprise',    slug: 'enterprise',   billing_model: 'custom',   price_monthly: '0',    max_sites: '-1', max_users: '-1', max_teams: '-1', trial_days: '0',  features: '["Tout Pro inclus","À partir de 100 utilisateurs","Tarif négocié","Account manager dédié","SLA garanti"]' },
];

const CheckIcon = () => (
  <svg className="lp-check-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const ChevronIcon = () => (
  <svg className="lp-faq-chevron" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

function MaintenancePage() {
  return (
    <div className="lp-maintenance">
      <div className="lp-maintenance-box">
        <div className="lp-maintenance-icon">🔧</div>
        <h1 className="lp-maintenance-title">Site en maintenance</h1>
        <p className="lp-maintenance-desc">
          Clockeo est temporairement indisponible pour maintenance.<br />
          Nous serons de retour très prochainement.
        </p>
      </div>
    </div>
  );
}

function IOSModal({ onClose }) {
  return (
    <div className="lp lp-ios-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lp-ios-modal">
        <button className="lp-ios-close" onClick={onClose}>✕</button>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🍎</div>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>Installer sur iPhone / iPad</div>
          <div style={{ fontSize: 13, color: '#94a3b8' }}>Depuis Safari sur cette page</div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            "Appuyez sur l'icône Partage ⬆ en bas de l'écran",
            'Faites défiler et appuyez sur "Sur l\'écran d\'accueil"',
            'Appuyez sur "Ajouter" en haut à droite',
          ].map((text, i) => (
            <div key={i} className="lp-ios-step">
              <div className="lp-ios-num">{i + 1}</div>
              <div style={{ fontSize: 14, color: '#cbd5e1', lineHeight: 1.4 }} dangerouslySetInnerHTML={{ __html: text }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const [menuOpen,    setMenuOpen]    = useState(false);
  const [openFaq,     setOpenFaq]     = useState(null);
  const [installState, setInstallState] = useState('idle');
  const [showIOSModal, setShowIOSModal] = useState(false);
  const [maintenance,  setMaintenance]  = useState(false);
  const [plans,        setPlans]        = useState(FALLBACK_PLANS);
  const [landingOptions, setLandingOptions] = useState([]);
  const deferredPrompt = useRef(null);

  // Config (polling toutes les 30s pour réagir à la maintenance)
  useEffect(() => {
    const checkConfig = () => {
      fetch(`${API}/app-config`)
        .then(r => r.json())
        .then(d => { setMaintenance(d?.data?.landing_maintenance === '1'); })
        .catch(() => {});
    };
    checkConfig();
    const interval = setInterval(checkConfig, 30000);
    return () => clearInterval(interval);
  }, []);

  // Plans + options (chargement unique)
  useEffect(() => {
    fetch(`${API}/plans`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => {
        const data = Array.isArray(d?.data) ? d.data.filter(p => p?.id) : [];
        if (data.length > 0) setPlans(data);
      })
      .catch(() => {});
    fetch(`${API}/options`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { if (Array.isArray(d?.data)) setLandingOptions(d.data); })
      .catch(() => {});
  }, []);

  // Scroll reveal — se réinitialise quand maintenance ou plans changent
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('visible'); }),
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );
    // Légère attente pour que React ait fini de mettre à jour le DOM
    const t = setTimeout(() => {
      document.querySelectorAll('.lp .reveal').forEach(el => observer.observe(el));
    }, 50);
    return () => { clearTimeout(t); observer.disconnect(); };
  }, [maintenance, plans]);

  // PWA install
  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || navigator.standalone;
    if (isStandalone) { setInstallState('installed'); return; }

    const isIOS    = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    if (isIOS && isSafari) { setInstallState('ios'); return; }

    const onPrompt = e => { e.preventDefault(); deferredPrompt.current = e; setInstallState('available'); };
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', () => setInstallState('installed'));
    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  const doInstall = async () => {
    if (!deferredPrompt.current) return;
    deferredPrompt.current.prompt();
    const { outcome } = await deferredPrompt.current.userChoice;
    deferredPrompt.current = null;
    if (outcome === 'accepted') setInstallState('installed');
  };

  const handleInstallClick = () => {
    if (installState === 'available') doInstall();
    else if (installState === 'ios')  setShowIOSModal(true);
  };

  const year = new Date().getFullYear();

  if (maintenance) return <MaintenancePage />;

  return (
    <div className={`lp${menuOpen ? ' lp-menu-open' : ''}`}>

      {/* NAV */}
      <nav>
        <div className="lp-container lp-nav-inner">
          <a href="#hero" className="lp-nav-logo">
            <img src="/assets/icon-192.png" alt="Clockeo" onError={e => { e.target.style.display = 'none'; }} />
            Clockeo
          </a>
          <div className="lp-nav-drawer">
            <ul className="lp-nav-links">
              {[['#features','Fonctionnalités'],['#how','Comment ça marche'],['#pricing','Tarifs'],['#install','Installer']].map(([href, label]) => (
                <li key={href}><a href={href} onClick={() => setMenuOpen(false)}>{label}</a></li>
              ))}
            </ul>
            <div className="lp-nav-cta">
              <AppLink to="https://clockeo.fr/login" className="lp-btn lp-btn-outline">Connexion</AppLink>
              {installState === 'installed'
                ? <a href="#install" className="lp-btn lp-btn-primary" style={{ opacity: 0.6, pointerEvents: 'none' }}>✓ Installée</a>
                : <a href="#install" className="lp-btn lp-btn-primary">Installer l'app</a>
              }
            </div>
          </div>
          <button className="lp-hamburger" aria-label="Menu" onClick={() => setMenuOpen(o => !o)}>
            <span className="lp-bar lp-bar-1" />
            <span className="lp-bar lp-bar-2" />
            <span className="lp-bar lp-bar-3" />
          </button>
        </div>
      </nav>

      {/* HERO */}
      <section className="lp-hero lp-section" id="hero">
        <div className="lp-hero-bg">
          <div className="lp-hero-orb lp-hero-orb-1" />
          <div className="lp-hero-orb lp-hero-orb-2" />
        </div>
        <div className="lp-container">
          <div className="lp-hero-grid">
            <div className="lp-hero-content">
              <div className="lp-hero-eyebrow reveal">
                <span className="lp-badge">
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="currentColor"><circle cx="6" cy="6" r="3" /></svg>
                  Disponible maintenant
                </span>
              </div>
              <h1 className="lp-hero-title reveal reveal-delay-1">
                Pointage terrain<br /><span className="gradient-text">intelligent</span>
              </h1>
              <p className="lp-hero-desc reveal reveal-delay-2">
                Gérez la présence de vos équipes en temps réel, depuis n'importe quel chantier ou site. QR code, géolocalisation, supervision live — tout en un.
              </p>
              <div className="lp-hero-actions reveal reveal-delay-3">
                <a href="#install" className="lp-btn lp-btn-primary lp-btn-lg">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v13M8 11l4 4 4-4"/><rect x="3" y="17" width="18" height="4" rx="2"/></svg>
                  Installer gratuitement
                </a>
                <a href="#features" className="lp-btn lp-btn-outline lp-btn-lg">Voir les fonctionnalités</a>
              </div>
              <div className="lp-hero-stats reveal reveal-delay-4">
                {[['3','Plans disponibles'],['2','Modes de pointage'],['100%','Mobile-first']].map(([val, label]) => (
                  <div key={label}>
                    <div className="lp-stat-value">{val}</div>
                    <div className="lp-stat-label">{label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="lp-hero-visual reveal reveal-delay-2">
              <div className="lp-phone">
                <div className="lp-phone-notch" />
                <div className="lp-phone-screen">
                  <div className="lp-phone-header">
                    <div className="lp-phone-title">Supervision live</div>
                    <div className="lp-phone-dot" />
                  </div>
                  <div className="lp-phone-card">
                    <div className="lp-phone-card-label">Équipe présente</div>
                    <div className="lp-phone-card-val">8 / 12</div>
                  </div>
                  <div className="lp-phone-mini-grid">
                    <div className="lp-phone-mini"><div className="lp-phone-mini-label">Sites actifs</div><div className="lp-phone-mini-val" style={{ color: '#38bdf8' }}>4</div></div>
                    <div className="lp-phone-mini"><div className="lp-phone-mini-label">Pointages</div><div className="lp-phone-mini-val" style={{ color: '#22c55e' }}>23</div></div>
                  </div>
                  {[['AL','Alex L.','2h 14m','linear-gradient(135deg,#0ea5e9,#0369a1)'],['MR','Marie R.','1h 47m','linear-gradient(135deg,#f59e0b,#ef4444)'],['JP','Jean P.','3h 02m','linear-gradient(135deg,#8b5cf6,#0ea5e9)']].map(([init, name, time, bg]) => (
                    <div key={init} className="lp-phone-row">
                      <div className="lp-phone-avatar" style={{ background: bg }}>{init}</div>
                      <div className="lp-phone-emp-name">{name}</div>
                      <div className="lp-phone-emp-time">{time}</div>
                      <div className="lp-phone-emp-badge">Sur site</div>
                    </div>
                  ))}
                  <div className="lp-phone-cta">Pointer — QR ou GPS</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LOGOS */}
      <div className="lp-logos">
        <div className="lp-container">
          <p className="lp-logos-label">Conçu pour les entreprises de terrain</p>
          <div className="lp-logos-row">
            {['Bâtiment','Nettoyage','Sécurité','Logistique','BTP','Services'].map(s => (
              <span key={s} className="lp-logo-item">{s}</span>
            ))}
          </div>
        </div>
      </div>

      {/* TESTIMONIALS */}
      <section className="lp-section" id="temoignages" style={{ paddingTop: 60, paddingBottom: 60 }}>
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-badge reveal" style={{ marginBottom: 16 }}>Témoignages</div>
            <h2 className="lp-section-title reveal reveal-delay-1">Ils ont simplifié leur pointage</h2>
            <p className="lp-section-sub reveal reveal-delay-2">Des entreprises de terrain qui ont gagné du temps et amélioré leur suivi des présences.</p>
          </div>
          <div className="lp-testimonials-grid">
            {[
              { quote: "Avant Clockeo, je passais 2 heures chaque lundi à reconstituer les heures de mes équipes depuis des WhatsApp. Maintenant j'exporte le CSV en 30 secondes.", name: 'Sophie L.', role: 'Directrice, entreprise de nettoyage · 18 employés', init: 'SL', bg: 'linear-gradient(135deg,#0ea5e9,#0369a1)', delay: '' },
              { quote: "Le QR code sur chaque chantier, c'est la révolution. Mes chefs d'équipe n'ont plus à noter quoi que ce soit. La supervision en live me rassure au quotidien.", name: 'Marc R.', role: 'Gérant, société BTP · 32 employés', init: 'MR', bg: 'linear-gradient(135deg,#f59e0b,#ef4444)', delay: 'reveal-delay-2' },
              { quote: "On a essayé 3 logiciels avant Clockeo. C'est le seul qui fonctionne vraiment sur le terrain — même sans connexion. Mes agents de sécurité adorent.", name: 'Karim D.', role: 'Responsable RH, agence de sécurité · 55 employés', init: 'KD', bg: 'linear-gradient(135deg,#8b5cf6,#6d28d9)', delay: 'reveal-delay-4' },
            ].map(t => (
              <div key={t.init} className={`lp-testimonial reveal ${t.delay}`}>
                <div className="lp-testimonial-quote">"{t.quote}"</div>
                <div className="lp-testimonial-author">
                  <div className="lp-testimonial-avatar" style={{ background: t.bg }}>{t.init}</div>
                  <div>
                    <div className="lp-testimonial-name">{t.name}</div>
                    <div className="lp-testimonial-role">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="lp-section" id="features">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-badge reveal" style={{ marginBottom: 16 }}>Fonctionnalités</div>
            <h2 className="lp-section-title reveal reveal-delay-1">Tout ce dont votre équipe a besoin</h2>
            <p className="lp-section-sub reveal reveal-delay-2">Du pointage terrain à la supervision en temps réel, Clockeo couvre l'ensemble du cycle de présence de vos équipes.</p>
          </div>
          <div className="lp-features-grid">
            {[
              { icon: '📱', title: 'Pointage QR Code', desc: 'Chaque site génère un QR code unique sécurisé. L\'employé scanne à son arrivée et à son départ — en moins de 5 secondes.', delay: '' },
              { icon: '📍', title: 'Géolocalisation GPS', desc: 'Validation automatique par GPS avec périmètre configurable par site (10 à 500m). Les pointages hors zone sont signalés.', delay: 'reveal-delay-1' },
              { icon: '👁️', title: 'Supervision temps réel', desc: 'Vue live de qui est sur site, depuis combien de temps. Compteur actualisé toutes les 30 secondes. Zéro appel, zéro doute.', delay: 'reveal-delay-2' },
              { icon: '🏢', title: 'Gestion multi-sites', desc: 'Créez autant de sites que nécessaire avec coordonnées GPS, rayon de géofencing et QR code dédié. Modification instantanée.', delay: 'reveal-delay-1' },
              { icon: '👥', title: 'Équipes & rôles', desc: 'Organisez vos employés en équipes avec manager attitré. Accès différencié par rôle : employé, manager, admin.', delay: 'reveal-delay-2' },
              { icon: '📊', title: 'Historique & exports', desc: 'Consultez l\'historique complet des pointages avec filtres avancés. Exportez en CSV pour votre paie ou vos reportings.', delay: 'reveal-delay-3' },
            ].map(f => (
              <div key={f.title} className={`lp-feature-card reveal ${f.delay}`}>
                <div className="lp-feature-icon">{f.icon}</div>
                <h3 className="lp-feature-title">{f.title}</h3>
                <p className="lp-feature-desc">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="lp-section lp-how-bg" id="how">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-badge reveal" style={{ marginBottom: 16 }}>Simple &amp; rapide</div>
            <h2 className="lp-section-title reveal reveal-delay-1">Opérationnel en 3 étapes</h2>
            <p className="lp-section-sub reveal reveal-delay-2">Pas de matériel à acheter, pas d'intégration complexe. Clockeo fonctionne depuis votre téléphone.</p>
          </div>
          <div className="lp-steps-grid">
            {[
              { n: 1, title: 'Créez votre entreprise', desc: 'Inscrivez-vous gratuitement en 1 minute. Configurez vos sites avec leurs coordonnées GPS et générez les QR codes.', delay: '' },
              { n: 2, title: 'Invitez votre équipe', desc: 'Créez les comptes de vos employés, assignez-les à leurs équipes et sites. Ils reçoivent leur accès immédiatement.', delay: 'reveal-delay-2' },
              { n: 3, title: 'Suivez en temps réel', desc: 'Vos équipes pointent via QR ou GPS. Vous supervisez les présences en direct depuis votre tableau de bord.', delay: 'reveal-delay-4' },
            ].map(s => (
              <div key={s.n} className={`lp-step reveal ${s.delay}`}>
                <div className="lp-step-num">{s.n}</div>
                <h3 className="lp-step-title">{s.title}</h3>
                <p className="lp-step-desc">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ROLES */}
      <section className="lp-section" id="roles">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-badge reveal" style={{ marginBottom: 16 }}>Pour chaque rôle</div>
            <h2 className="lp-section-title reveal reveal-delay-1">Une interface adaptée à chacun</h2>
            <p className="lp-section-sub reveal reveal-delay-2">Clockeo s'adapte au rôle de chaque utilisateur. Les employés pointent, les managers supervisent, les admins pilotent.</p>
          </div>
          <div className="lp-roles-grid">
            {[
              { icon: '👷', title: 'Employé terrain', sub: 'Pointage simple et rapide', features: ['Pointage entrée / sortie par QR code','Validation GPS automatique','Historique personnel de présence','Sélection du site en 1 tap','Fonctionne hors connexion (PWA)'], style: {}, delay: '' },
              { icon: '📋', title: 'Manager', sub: 'Supervision & organisation', features: ['Supervision en temps réel des équipes','Gestion des sites et QR codes','Création et gestion des équipes','Historique complet des pointages','Export CSV pour la paie'], style: { borderColor: 'rgba(14,165,233,0.3)' }, delay: 'reveal-delay-2' },
              { icon: '🛡️', title: 'Administrateur', sub: 'Contrôle total', features: ['Gestion complète des utilisateurs','Attribution des rôles et accès','Configuration du géofencing global','Gestion des abonnements et plans','Tickets support dédiés'], style: {}, delay: 'reveal-delay-4' },
            ].map(r => (
              <div key={r.title} className={`lp-role-card reveal ${r.delay}`} style={r.style}>
                <div className="lp-role-icon">{r.icon}</div>
                <h3 className="lp-role-title">{r.title}</h3>
                <p className="lp-role-subtitle">{r.sub}</p>
                <ul className="lp-role-features">{r.features.map(f => <li key={f}>{f}</li>)}</ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="lp-section lp-pricing-bg" id="pricing">
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-badge reveal" style={{ marginBottom: 16 }}>Tarifs</div>
            <h2 className="lp-section-title reveal reveal-delay-1">Simple et transparent</h2>
            <p className="lp-section-sub reveal reveal-delay-2">Commencez gratuitement, évoluez quand vous le souhaitez. Sans engagement, sans surprise.</p>
          </div>
          <div className="lp-pricing-grid">
            {plans.map((plan, idx) => {
              const model    = plan.billing_model || 'fixed';
              const isCustom = model === 'custom';
              const isPerUser= model === 'per_user';
              const isTiered = model === 'tiered';
              const isFeatured = plan.slug === 'pro' || (plans.length === 3 && idx === 1);
              const isFree   = !isCustom && parseFloat(plan.price_monthly) === 0 && !isPerUser;
              const isLast   = isCustom;
              const quota    = (v) => parseInt(v, 10) === -1 ? '∞' : String(parseInt(v, 10));
              const delays   = ['', 'reveal-delay-2', 'reveal-delay-4'];
              const trialDays = parseInt(plan.trial_days) || 0;
              let features = [];
              try { features = typeof plan.features === 'string' ? JSON.parse(plan.features) : (plan.features || []); } catch {}

              const fmtPrice = (n) => parseFloat(n).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

              // Affichage du prix selon le modèle
              const renderPrice = () => {
                if (isCustom) return (
                  <div style={{ marginBottom: 8 }}>
                    <span style={{ fontSize: 26, fontWeight: 800, color: '#94a3b8' }}>Sur devis</span>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>À partir de 100 utilisateurs</div>
                  </div>
                );
                if (isPerUser) return (
                  <div style={{ marginBottom: 8 }}>
                    <span className={`lp-pricing-amount${isFeatured ? ' gradient-text' : ''}`}>
                      {fmtPrice(plan.price_monthly)}€
                    </span>
                    <span className="lp-pricing-period"> / mois</span>
                    <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4, fontWeight: 600 }}>
                      par utilisateur actif
                    </div>
                  </div>
                );
                if (isTiered) return (
                  <div style={{ marginBottom: 8 }}>
                    <div>
                      <span className={`lp-pricing-amount${isFeatured ? ' gradient-text' : ''}`}>
                        {fmtPrice(plan.price_monthly)}€
                      </span>
                      <span className="lp-pricing-period"> / mois</span>
                    </div>
                    {parseInt(plan.included_users) > 0 && (
                      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
                        {plan.included_users} utilisateurs inclus
                        {parseFloat(plan.price_per_extra_user) > 0 && (
                          <> · <strong style={{ color: '#f1f5f9' }}>+{fmtPrice(plan.price_per_extra_user)}€</strong>/util. supp.
                        </>)}
                      </div>
                    )}
                  </div>
                );
                // fixed / gratuit
                return (
                  <div style={{ marginBottom: 8 }}>
                    <span className={`lp-pricing-amount${isFeatured ? ' gradient-text' : ''}`}>
                      {isFree ? 'Gratuit' : `${fmtPrice(plan.price_monthly)}€`}
                    </span>
                    {!isFree && <span className="lp-pricing-period"> / mois</span>}
                  </div>
                );
              };

              return (
                <div key={plan.id} className={`lp-pricing-card${isFeatured ? ' featured' : ''} reveal ${delays[idx] || ''}`}>
                  {isFeatured && <div className="lp-pricing-popular">Populaire</div>}
                  {trialDays > 0 && (
                    <div style={{ position:'absolute', top: isFeatured ? 48 : 18, right: 18, background:'rgba(34,197,94,0.15)', color:'#22c55e', fontSize:11, fontWeight:700, padding:'4px 12px', borderRadius:9999, border:'1px solid rgba(34,197,94,0.3)', whiteSpace:'nowrap' }}>
                      ✓ {trialDays} jours gratuits
                    </div>
                  )}
                  <div className="lp-pricing-plan">{plan.name}</div>
                  {renderPrice()}
                  {plan.description && <p className="lp-pricing-desc">{plan.description}</p>}
                  {!isCustom && !isPerUser && (
                    <div className="lp-pricing-limits">
                      <div className="lp-pricing-limit"><span className="lp-pricing-limit-val">{quota(plan.max_sites)}</span><span className="lp-pricing-limit-label">Sites</span></div>
                      <div className="lp-pricing-limit"><span className="lp-pricing-limit-val">{quota(plan.max_users)}</span><span className="lp-pricing-limit-label">Utilisateurs</span></div>
                      <div className="lp-pricing-limit"><span className="lp-pricing-limit-val">{quota(plan.max_teams)}</span><span className="lp-pricing-limit-label">Équipes</span></div>
                    </div>
                  )}
                  {features.length > 0 && (
                    <ul className="lp-pricing-features">
                      {features.map((f, i) => <li key={i}><CheckIcon />{f}</li>)}
                    </ul>
                  )}
                  {isLast
                    ? <a href="mailto:contact@clockeo.fr" className="lp-pricing-cta lp-pricing-cta-outline" style={{ textAlign:'center' }}>
                        ✉️ Nous contacter
                      </a>
                    : <AppLink to="/register" className={`lp-pricing-cta ${isFeatured ? 'lp-pricing-cta-primary' : 'lp-pricing-cta-outline'}`} style={{ textAlign:'center' }}>
                        {trialDays > 0
                          ? `Essayer ${trialDays} jours gratuits →`
                          : isFree ? 'Commencer gratuitement →' : `Démarrer avec ${plan.name} →`}
                      </AppLink>
                  }
                  {trialDays > 0 && !isCustom && (
                    <div style={{ textAlign:'center', fontSize:11, color:'#64748b', marginTop:10 }}>
                      Sans carte bancaire · Annulation libre
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Section options si disponibles */}
          {landingOptions.length > 0 && (
            <div style={{ marginTop: 48 }}>
              <div className="lp-section-header" style={{ marginBottom: 32 }}>
                <div className="lp-badge" style={{ marginBottom: 12 }}>Options</div>
                <h3 className="lp-section-title" style={{ fontSize: 'clamp(22px,3vw,32px)' }}>Enrichissez votre abonnement</h3>
                <p className="lp-section-sub">Des modules complémentaires activables à tout moment sur votre compte.</p>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
                {landingOptions.map(opt => {
                  let optFeatures = [];
                  try { optFeatures = typeof opt.features === 'string' ? JSON.parse(opt.features) : (opt.features || []); } catch {}
                  return (
                  <div key={opt.id} className="lp-feature-card reveal" style={{ background: 'rgba(14,165,233,0.04)', borderColor: 'rgba(14,165,233,0.15)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12 }}>
                      <h4 className="lp-feature-title" style={{ marginBottom: 0 }}>{opt.name}</h4>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        {opt.price_onetime != null && (
                          <div style={{ fontSize: 17, fontWeight: 800, color: '#38bdf8' }}>{parseFloat(opt.price_onetime).toLocaleString('fr-FR')}€ <span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>paiement unique</span></div>
                        )}
                        {opt.price_monthly != null && (
                          <div style={{ fontSize: 17, fontWeight: 800, color: '#38bdf8' }}>{parseFloat(opt.price_monthly).toLocaleString('fr-FR')}€<span style={{ fontSize: 11, fontWeight: 400, color: '#94a3b8' }}>/mois</span></div>
                        )}
                        {opt.included_users != null && opt.price_per_extra_user != null && (
                          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>jusqu'à {opt.included_users} salariés<br/>+{parseFloat(opt.price_per_extra_user).toLocaleString('fr-FR')}€/salarié supp.</div>
                        )}
                      </div>
                    </div>
                    {opt.description && <p className="lp-feature-desc" style={{ marginBottom: optFeatures.length ? 12 : 0 }}>{opt.description}</p>}
                    {optFeatures.length > 0 && (
                      <ul className="lp-pricing-features" style={{ marginBottom: 0 }}>
                        {optFeatures.map((f, i) => <li key={i}><CheckIcon />{f}</li>)}
                      </ul>
                    )}
                  </div>
                );})}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="lp-section" id="faq" style={{ paddingTop: 60, paddingBottom: 60 }}>
        <div className="lp-container">
          <div className="lp-section-header">
            <div className="lp-badge reveal" style={{ marginBottom: 16 }}>FAQ</div>
            <h2 className="lp-section-title reveal reveal-delay-1">Questions fréquentes</h2>
            <p className="lp-section-sub reveal reveal-delay-2">Tout ce que vous devez savoir avant de démarrer.</p>
          </div>
          <div className="lp-faq-list reveal">
            {[
              { q: 'Faut-il télécharger une application ?', a: "Non, Clockeo est une Progressive Web App (PWA). Il suffit d'ouvrir l'adresse dans votre navigateur et d'ajouter l'icône à votre écran d'accueil — sans passer par l'App Store ou le Play Store. Aucun téléchargement, aucune mise à jour manuelle." },
              { q: "Est-ce que ça fonctionne sans internet ?", a: "Oui. L'application PWA continue de fonctionner même sans connexion grâce au service worker. Dès que la connexion est rétablie, les données sont synchronisées automatiquement." },
              { q: 'Comment fonctionne le pointage par QR code ?', a: "Chaque site dispose d'un QR code unique généré automatiquement. L'employé ouvre l'application, scanne le code affiché sur le site et le pointage est enregistré instantanément. L'entrée et la sortie sont détectées automatiquement selon le dernier état de l'employé." },
              { q: 'Mes données sont-elles sécurisées et conformes au RGPD ?', a: "Oui. Vos données sont stockées en Europe, chiffrées en transit (HTTPS) et au repos. Les tokens d'authentification expirent automatiquement. Nous ne revendons aucune donnée." },
              { q: 'Puis-je changer de plan à tout moment ?', a: "Oui. Vous pouvez demander un changement de plan depuis les paramètres de votre espace. Le plan Gratuit est sans limite de durée. Les plans Pro et Entreprise sont sans engagement minimum." },
              { q: 'Combien de temps faut-il pour démarrer ?', a: "Moins de 10 minutes. Créez votre espace, ajoutez votre premier site avec ses coordonnées GPS, créez vos employés et imprimez le QR code. Votre équipe peut pointer dès la première heure." },
              { q: 'Est-ce disponible sur iPhone ?', a: "Oui, Clockeo fonctionne sur iPhone et iPad via Safari (iOS 16.4 et ultérieur). Ouvrez l'application dans Safari, appuyez sur le bouton Partage, puis \"Sur l'écran d'accueil\"." },
            ].map((item, i) => (
              <div key={i} className={`lp-faq-item${openFaq === i ? ' open' : ''}`}>
                <button className="lp-faq-question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {item.q}
                  <ChevronIcon />
                </button>
                <div className="lp-faq-answer"><p>{item.a}</p></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INSTALL */}
      <section className="lp-section" id="install">
        <div className="lp-container">
          <div className="lp-install-inner">
            <div className="lp-install-grid">
              <div>
                <div className="lp-badge reveal" style={{ marginBottom: 20 }}>Application mobile</div>
                <h2 className="lp-install-title reveal reveal-delay-1">Installez Clockeo<br />sur votre téléphone</h2>
                <p className="lp-install-desc reveal reveal-delay-2">
                  Clockeo est une Progressive Web App (PWA). Pas de store, pas de téléchargement. Ajoutez-la directement à votre écran d'accueil en un tap.
                </p>
                <div className="reveal reveal-delay-3">
                  {installState === 'installed' ? (
                    <div className="lp-installed-msg">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                      Application déjà installée !
                    </div>
                  ) : (installState === 'available' || installState === 'ios') ? (
                    <button className="lp-install-btn" onClick={handleInstallClick}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v13M8 11l4 4 4-4"/><rect x="3" y="17" width="18" height="4" rx="2"/></svg>
                      Installer Clockeo
                    </button>
                  ) : null}
                  <AppLink to="/login" className="lp-install-app-link">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    Ouvrir l'application dans le navigateur
                  </AppLink>
                  <p className="lp-install-note">
                    {installState === 'idle' ? "Le bouton d'installation apparaît automatiquement sur Chrome et Edge." : installState === 'ios' ? "Suivez les instructions pour ajouter Clockeo à votre écran d'accueil." : ''}
                  </p>
                </div>
              </div>
              <div className="lp-platforms reveal reveal-delay-2">
                <div className="lp-platform">
                  <div className="lp-platform-header">
                    <div><div className="lp-platform-name">Android — Chrome</div><div className="lp-platform-sub">Recommandé</div></div>
                    <span className="lp-tag" style={{ marginLeft: 'auto' }}>Auto</span>
                  </div>
                  <ol className="lp-platform-steps">
                    <li><span className="lp-step-badge">1</span>Ouvrez Clockeo dans Chrome</li>
                    <li><span className="lp-step-badge">2</span>Tapez le bouton <strong>"Installer Clockeo"</strong> — ou attendez la bannière Chrome</li>
                    <li><span className="lp-step-badge">3</span>Confirmez l'installation dans la boîte de dialogue</li>
                  </ol>
                </div>
                <div className="lp-platform">
                  <div className="lp-platform-header">
                    <div><div className="lp-platform-name">iPhone &amp; iPad — Safari</div><div className="lp-platform-sub">iOS 16.4+</div></div>
                  </div>
                  <ol className="lp-platform-steps">
                    <li><span className="lp-step-badge">1</span>Ouvrez Clockeo dans <strong>Safari</strong></li>
                    <li><span className="lp-step-badge">2</span>Appuyez sur l'icône <strong>Partage ⬆</strong> en bas de l'écran</li>
                    <li><span className="lp-step-badge">3</span>Faites défiler et appuyez sur <strong>"Sur l'écran d'accueil"</strong></li>
                    <li><span className="lp-step-badge">4</span>Confirmez en appuyant sur <strong>"Ajouter"</strong></li>
                  </ol>
                </div>
                <div className="lp-platform">
                  <div className="lp-platform-header">
                    <div><div className="lp-platform-name">Desktop — Chrome &amp; Edge</div><div className="lp-platform-sub">Windows, macOS, Linux</div></div>
                  </div>
                  <ol className="lp-platform-steps">
                    <li><span className="lp-step-badge">1</span>Cliquez sur le bouton <strong>"Installer Clockeo"</strong></li>
                    <li><span className="lp-step-badge">2</span>Ou cliquez sur l'icône <strong>⊕</strong> dans la barre d'adresse</li>
                    <li><span className="lp-step-badge">3</span>Confirmez — Clockeo s'ouvre comme une app native</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="lp-cta-section">
        <div className="lp-container">
          <div className="lp-cta-inner reveal">
            <h2 className="lp-cta-title">Prêt à simplifier le pointage<br />de votre équipe ?</h2>
            <p className="lp-cta-desc">Démarrez gratuitement, sans carte bancaire. Opérationnel en moins de 10 minutes.</p>
            <div className="lp-cta-actions">
              <AppLink to="https://clockeo.fr/register" className="lp-btn lp-btn-white lp-btn-xl">Créer mon compte gratuit</AppLink>
              <a href="#install" className="lp-btn lp-btn-white-outline lp-btn-xl">Installer l'application</a>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer>
        <div className="lp-container">
          <div className="lp-footer-grid">
            <div>
              <div className="lp-footer-brand">Clockeo</div>
              <p className="lp-footer-brand-desc">Application de pointage terrain pour équipes. QR code, GPS, supervision temps réel.</p>
            </div>
            <div>
              <div className="lp-footer-col-title">Produit</div>
              <ul className="lp-footer-links">
                <li><a href="#features">Fonctionnalités</a></li>
                <li><a href="#pricing">Tarifs</a></li>
                <li><a href="#faq">FAQ</a></li>
                <li><a href="#install">Installer</a></li>
                <li><a href="https://clockeo.fr/login">Connexion</a></li>
              </ul>
            </div>
            <div>
              <div className="lp-footer-col-title">Utilisation</div>
              <ul className="lp-footer-links">
                <li><a href="#roles">Employés</a></li>
                <li><a href="#roles">Managers</a></li>
                <li><a href="#roles">Administrateurs</a></li>
                <li><a href="#how">Comment ça marche</a></li>
              </ul>
            </div>
            <div>
              <div className="lp-footer-col-title">Contact</div>
              <ul className="lp-footer-links">
                <li><a href="mailto:contact@clockeo.fr">contact@clockeo.fr</a></li>
                <li><a href="https://clockeo.fr/register">Inscription</a></li>
              </ul>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <p className="lp-footer-copy">© {year} Clockeo. Tous droits réservés.</p>
            <div className="lp-footer-legal">
              <a href="/mentions-legales">Mentions légales</a>
              <a href="/cgu">CGU</a>
              <a href="/confidentialite">Confidentialité</a>
            </div>
          </div>
        </div>
      </footer>

      {showIOSModal && <IOSModal onClose={() => setShowIOSModal(false)} />}
    </div>
  );
}
